<template>
  <div class="post-card" :class="{ pinned: post.is_pinned }">
    <!-- Pinned Badge -->
    <div v-if="post.is_pinned" class="pinned-badge">
      <svg viewBox="0 0 24 24" width="16" height="16" fill="#65676b">
        <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
      </svg>
      <span>Pinned Post</span>
    </div>

    <!-- Post Header -->
    <div class="post-header">
      <img :src="authorAvatar" :alt="post.author_name" class="author-avatar" />
      <div class="author-info">
        <span class="author-name">{{ post.author_name }}</span>
        <div class="post-meta">
          <span class="author-role">{{ post.author_role }}</span>
          <span class="separator">-</span>
          <span class="post-date">{{ formatDate(post.created_at) }}</span>
          <span v-if="post.updated_at" class="edited-label">(edited)</span>
        </div>
      </div>

      <!-- Post Actions Menu -->
      <div v-if="canModerate || isAuthor" class="post-actions-menu">
        <button class="menu-toggle" @click="showMenu = !showMenu">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="#65676b">
            <circle cx="12" cy="5" r="2"/>
            <circle cx="12" cy="12" r="2"/>
            <circle cx="12" cy="19" r="2"/>
          </svg>
        </button>
        <div v-if="showMenu" class="menu-dropdown" @click.stop>
          <button v-if="canModerate" @click="togglePin">
            {{ post.is_pinned ? 'Unpin Post' : 'Pin Post' }}
          </button>
          <button v-if="isAuthor" @click="editPost">Edit Post</button>
          <button class="delete-action" @click="deletePost">Delete Post</button>
        </div>
      </div>
    </div>

    <!-- Post Content -->
    <div class="post-content">
      <div v-if="isEditing" class="edit-content">
        <textarea
          v-model="editedContent"
          class="edit-textarea"
          @input="autoResize"
        ></textarea>
        <div class="edit-actions">
          <button class="cancel-btn" @click="cancelEdit">Cancel</button>
          <button class="save-btn" @click="saveEdit">Save</button>
        </div>
      </div>
      <p v-else class="content-text" v-html="formattedContent"></p>
    </div>

    <!-- Post Attachments -->
    <PostAttachments
      v-if="post.attachments && post.attachments.length > 0"
      :attachments="post.attachments"
    />

    <!-- Reactions Summary -->
    <div v-if="hasReactions" class="reactions-summary">
      <div class="reaction-icons">
        <span
          v-for="emoji in topReactions"
          :key="emoji"
          class="reaction-icon"
        >
          {{ emoji }}
        </span>
      </div>
      <span class="reaction-count">{{ totalReactions }}</span>
    </div>

    <!-- Divider -->
    <div class="post-divider"></div>

    <!-- Action Bar -->
    <div class="action-bar">
      <ReactionBar
        :reactions="post.reactions || {}"
        :user-id="userData.id"
        @toggle-reaction="handleReactionToggle"
      />
      <button class="action-btn" @click="toggleComments">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="#65676b">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10z"/>
        </svg>
        <span>Comment</span>
        <span v-if="post.comment_count > 0" class="count">
          ({{ post.comment_count }})
        </span>
      </button>
    </div>

    <!-- Comments Section -->
    <ThreadedComments
      v-if="showComments"
      :post-id="post.id"
      :user-data="userData"
      @comment-added="handleCommentAdded"
    />
  </div>
</template>

<script>
import { TeamPosts } from "@/services/team-posts-service";
import PostAttachments from "./PostAttachments.vue";
import ReactionBar from "./ReactionBar.vue";
import ThreadedComments from "../Comments/ThreadedComments.vue";
import { avatarUrl } from "@/utils/avatar";

export default {
  name: "PostCard",
  components: {
    PostAttachments,
    ReactionBar,
    ThreadedComments,
  },
  props: {
    post: {
      type: Object,
      required: true,
    },
    userData: {
      type: Object,
      required: true,
    },
    canModerate: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["post-updated", "post-deleted", "reaction-toggled"],
  data() {
    return {
      showMenu: false,
      showComments: false,
      isEditing: false,
      editedContent: "",
    };
  },
  computed: {
    authorAvatar() {
      if (this.post.author_image) {
        return this.getImgUrl(this.post.author_image);
      }
      return this.getImgUrl("default-image.png");
    },
    isAuthor() {
      return String(this.post.author_id) === String(this.userData.id);
    },
    formattedContent() {
      if (!this.post.content) return "";
      // The output is rendered via v-html, so user content MUST be HTML-
      // escaped before any markup (line breaks, links) is added — otherwise
      // a post containing `<script>` or `<img onerror>` executes in every
      // viewer's browser.
      const escaped = this.post.content
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
      return escaped
        .replace(/\n/g, "<br>")
        .replace(
          /(https?:\/\/[^\s<]+)/g,
          '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
        );
    },
    hasReactions() {
      return this.totalReactions > 0;
    },
    totalReactions() {
      if (!this.post.reactions) return 0;
      return Object.values(this.post.reactions).reduce(
        (sum, users) => sum + (Array.isArray(users) ? users.length : 0),
        0
      );
    },
    topReactions() {
      if (!this.post.reactions) return [];
      // Get top 3 reactions by count
      return Object.entries(this.post.reactions)
        .filter(([, users]) => Array.isArray(users) && users.length > 0)
        .sort((a, b) => b[1].length - a[1].length)
        .slice(0, 3)
        .map(([emoji]) => emoji);
    },
  },
  methods: {
    getImgUrl(imageName) {
      return avatarUrl(imageName);
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m`;
      if (diffHours < 24) return `${diffHours}h`;
      if (diffDays < 7) return `${diffDays}d`;

      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      });
    },

    toggleComments() {
      this.showComments = !this.showComments;
    },

    async togglePin() {
      this.showMenu = false;
      try {
        await TeamPosts.pinPost(this.post.id, !this.post.is_pinned);
        this.$emit("post-updated", {
          ...this.post,
          is_pinned: !this.post.is_pinned,
        });
      } catch (error) {
        console.error("Error toggling pin:", error);
      }
    },

    editPost() {
      this.showMenu = false;
      this.editedContent = this.post.content;
      this.isEditing = true;
    },

    cancelEdit() {
      this.isEditing = false;
      this.editedContent = "";
    },

    async saveEdit() {
      if (!this.editedContent.trim()) return;

      try {
        const response = await TeamPosts.updatePost(
          this.post.id,
          this.editedContent,
          this.userData.id
        );

        if (response.data.success) {
          this.$emit("post-updated", response.data.post);
          this.isEditing = false;
          this.editedContent = "";
        }
      } catch (error) {
        console.error("Error updating post:", error);
        alert("Failed to update post");
      }
    },

    async deletePost() {
      this.showMenu = false;
      if (!confirm("Are you sure you want to delete this post?")) return;

      try {
        const response = await TeamPosts.deletePost(this.post.id);
        if (response.data.success) {
          this.$emit("post-deleted", this.post.id);
        }
      } catch (error) {
        console.error("Error deleting post:", error);
        alert("Failed to delete post");
      }
    },

    async handleReactionToggle(emoji) {
      try {
        const response = await TeamPosts.togglePostReaction(
          this.post.id,
          emoji,
          this.userData.id
        );

        if (response.data.success) {
          this.$emit("reaction-toggled", {
            postId: this.post.id,
            reactions: response.data.reactions,
          });
        }
      } catch (error) {
        console.error("Error toggling reaction:", error);
      }
    },

    handleCommentAdded() {
      // Update comment count
      this.$emit("post-updated", {
        ...this.post,
        comment_count: (this.post.comment_count || 0) + 1,
      });
    },

    autoResize(event) {
      const textarea = event.target;
      textarea.style.height = "auto";
      textarea.style.height = textarea.scrollHeight + "px";
    },
    // Stable handler reference so beforeUnmount can remove the listener.
    // Without this, every PostCard mounted left a global listener attached
    // forever (one per post, growing across navigation).
    handleDocumentClick() {
      this.showMenu = false;
    },
  },
  mounted() {
    document.addEventListener("click", this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick);
  },
};
</script>

<style scoped>
.post-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.post-card.pinned {
  border: 2px solid #1877f2;
}

.pinned-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #f0f2f5;
  font-size: 13px;
  color: #65676b;
  font-weight: 500;
}

.post-header {
  display: flex;
  align-items: flex-start;
  padding: 16px 16px 0;
}

.author-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 12px;
}

.author-info {
  flex: 1;
}

.author-name {
  font-weight: 600;
  color: #050505;
  font-size: 15px;
}

.post-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #65676b;
  margin-top: 2px;
}

.author-role {
  background: #e4e6eb;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
}

.separator {
  color: #bcc0c4;
}

.edited-label {
  font-style: italic;
}

.post-actions-menu {
  position: relative;
}

.menu-toggle {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.menu-toggle:hover {
  background: #f0f2f5;
}

.menu-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  min-width: 150px;
  z-index: 10;
  overflow: hidden;
}

.menu-dropdown button {
  display: block;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  font-size: 15px;
  color: #050505;
  cursor: pointer;
}

.menu-dropdown button:hover {
  background: #f0f2f5;
}

.menu-dropdown .delete-action {
  color: #e41e3f;
}

.post-content {
  padding: 12px 16px 16px;
}

.content-text {
  font-size: 15px;
  line-height: 1.5;
  color: #050505;
  margin: 0;
  word-wrap: break-word;
}

.content-text :deep(a) {
  color: #1877f2;
}

.edit-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.edit-textarea {
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: 1px solid #dddfe2;
  border-radius: 8px;
  font-size: 15px;
  resize: none;
  outline: none;
}

.edit-textarea:focus {
  border-color: #1877f2;
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.cancel-btn,
.save-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.cancel-btn {
  background: #e4e6eb;
  color: #050505;
}

.save-btn {
  background: #1877f2;
  color: #fff;
}

.reactions-summary {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  gap: 6px;
}

.reaction-icons {
  display: flex;
}

.reaction-icon {
  font-size: 18px;
  margin-right: -4px;
}

/* Pill-style count badge to match the .games-count-badge convention used
   on every other tab (Trialists / Games / Training / Members). */
.reaction-count {
  display: inline-block;
  min-width: 1.5rem;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  background: #e7f1ff;
  color: #084298;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  margin-left: 4px;
}

.post-divider {
  height: 1px;
  background: #e4e6eb;
  margin: 0 16px;
}

.action-bar {
  display: flex;
  padding: 4px 16px;
}

.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border: none;
  background: none;
  color: #65676b;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 4px;
}

.action-btn:hover {
  background: #f0f2f5;
}

.action-btn .count {
  font-weight: 400;
}

@media (max-width: 768px) {
  .post-header {
    padding: 12px 12px 0;
  }

  .post-content {
    padding: 12px;
  }

  .action-bar {
    padding: 4px 8px;
  }

  .action-btn span:not(.count) {
    display: none;
  }
}
</style>
